import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import useConnectedCalendars from '../../hooks/useConnectedCalendars';
import { db } from '../../config/firebaseConfig';
import { doc, getDoc, updateDoc, setDoc, arrayUnion, arrayRemove } from 'firebase/firestore';
import FavoriteIcon from '@mui/icons-material/Favorite';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import './YachtCard.css';

const YachtCard = ({ yacht }) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isFavorite, setIsFavorite] = useState(false);
  const [reviewStats, setReviewStats] = useState({
    totalNumberOfReviews: 0,
    averageRating: 0
  });
  const { user } = useAuth();
  const connectedCalendars = useConnectedCalendars();
  // Only yachts with a connected Google Calendar can be booked instantly -
  // their availability is checked live instead of waiting for the owner.
  const isInstantBook = connectedCalendars.has(yacht.id);

  useEffect(() => {
    const fetchReviewStats = async () => {
      try {
        const reviewStatsRef = doc(db, 'reviews', yacht.id);
        const reviewStatsDoc = await getDoc(reviewStatsRef);
        
        if (reviewStatsDoc.exists()) {
          const data = reviewStatsDoc.data();
          setReviewStats({
            totalNumberOfReviews: data.totalNumberOfReviews || 0,
            averageRating: data.averageRating ? Number(data.averageRating.toFixed(1)) : 0
          });
        }
      } catch (error) {
        console.error('Error fetching review stats:', error);
      }
    };

    fetchReviewStats();
  }, [yacht.id]);

  useEffect(() => {
    const checkIfFavorite = async () => {
      if (!user) return;
      
      try {
        const favoritesRef = doc(db, 'favorites', user.uid);
        const favoritesDoc = await getDoc(favoritesRef);
        
        if (favoritesDoc.exists()) {
          const favorites = favoritesDoc.data().yachtIds || [];
          setIsFavorite(favorites.includes(yacht.id));
        }
      } catch (error) {
        console.error('Error checking favorite status:', error);
      }
    };

    checkIfFavorite();
  }, [user, yacht.id]);

  const toggleFavorite = async (e) => {
    e.preventDefault(); // Prevent navigation when clicking the favorite button
    if (!user) return;

    try {
      const favoritesRef = doc(db, 'favorites', user.uid);
      const favoritesDoc = await getDoc(favoritesRef);

      if (!favoritesDoc.exists()) {
        // Create favorites document if it doesn't exist
        await setDoc(favoritesRef, { yachtIds: [yacht.id] });
        setIsFavorite(true);
      } else {
        // Update existing favorites
        await updateDoc(favoritesRef, {
          yachtIds: isFavorite
            ? arrayRemove(yacht.id)
            : arrayUnion(yacht.id)
        });
        setIsFavorite(!isFavorite);
      }
    } catch (error) {
      console.error('Error updating favorites:', error);
    }
  };

  const nextImage = (e) => {
    e.preventDefault();
    setCurrentImageIndex((prevIndex) => 
      prevIndex === yacht.images.length - 1 ? 0 : prevIndex + 1
    );
  };

  const prevImage = (e) => {
    e.preventDefault();
    setCurrentImageIndex((prevIndex) => 
      prevIndex === 0 ? yacht.images.length - 1 : prevIndex - 1
    );
  };

  return (
    <Link to={`/yacht/${yacht.id}`} className="yacht-card">
      <div className="yacht-card-image-container">
        {isInstantBook && <div className="instant-reserve">⚡ Instant Book</div>}
        {user && (
          <button 
            className={`favorite-button ${isFavorite ? 'favorited' : ''}`}
            onClick={toggleFavorite}
          >
            {isFavorite ? <FavoriteIcon sx={{ color: '#ff1744' }} /> : <FavoriteBorderIcon sx={{ color: '#757575' }} />}
          </button>
        )}
        <img 
          src={yacht.images[currentImageIndex]} 
          alt={yacht.name} 
          className="yacht-image" 
          loading="lazy"
        />
        <button className="nav-button prev" onClick={prevImage}>❮</button>
        <button className="nav-button next" onClick={nextImage}>❯</button>
        <div className="image-dots">
          {yacht.images.map((_, index) => (
            <span 
              key={index} 
              className={`dot ${index === currentImageIndex ? 'active' : ''}`}
              onClick={(e) => {
                e.preventDefault();
                setCurrentImageIndex(index);
              }}
            />
          ))}
        </div>
      </div>
      <div className="yacht-info">
        <div className="yacht-location">
          <h3>{yacht.name}</h3>
          <div className="rating">
            <span className="star">★</span>
            <span className="rating-score">{reviewStats.averageRating}</span>
            <span className="review-count">({reviewStats.totalNumberOfReviews})</span>
          </div>
        </div>
        <p className="yacht-type">{yacht.type} by {yacht.owner}</p>
        <p className="yacht-details">
          {yacht.specifications.length}m • Up to {yacht.totalCapacity} guests • Min rental {yacht.minDuration} {yacht.minDuration == '1' ? "hour" : "hours"}
        </p>
        <p className="yacht-location-text">{yacht.location}</p>
        <div className="yacht-price">
          <span className="price">€{yacht.price.toLocaleString('en-US')}</span>
          <span className="price-period">/hour</span>
        </div>
      </div>
    </Link>
  );
};

export default YachtCard;
